import React from "react";
import { connect } from "react-redux";
import { createSelector } from "reselect";

import PerformanceSummary from "./performanceSummary";
import SubmittedFeedbacks from "./submittedFeedbacks";
import {
  buildGeneralSkillsMap,
  buildSkillCategorizedMap,
  buildEventRolesMap
} from "./utils/feedbackAdapter";

const RATING_GROUPS = ["general_skills", "event_roles", "skills"];

const toOneDecimal = (value) => Number(Number(value || 0).toFixed(1));

const selectPerformanceOverview = createSelector(
  [
    (state) => state.talent.performanceOverview,
    (state) => state.talent?.generalSkillAttributes,
    (state) => state.talent?.skillCategorized,
    (state) => state.talent?.eventRoles
  ],
  (overview, generalSkillAttributes, skillCategorized, eventRoles) => {
    if (!overview) return {};

    const labelMaps = {
      general_skills: buildGeneralSkillsMap(generalSkillAttributes),
      event_roles: buildEventRolesMap(eventRoles),
      skills: buildSkillCategorizedMap(skillCategorized)
    };

    const ratings = {};
    const skillEntries = [];

    RATING_GROUPS.forEach((group) => {
      ratings[group] = {};

      Object.entries(overview[group] || {}).forEach(([uuid, stat]) => {
        ratings[group][uuid] = stat?.derived_rating;

        const label = labelMaps[group][uuid];
        if (label && stat?.number_of_ratings) {
          skillEntries.push({
            label,
            value: toOneDecimal(stat.derived_rating).toFixed(1),
            count: stat.number_of_ratings
          });
        }
      });
    });

    // Overall = weighted average of general skills, which are rated on every review
    const generalStats = Object.values(overview.general_skills || {});
    const ratingsTotal = generalStats.reduce((sum, stat) => sum + (stat?.ratings_total || 0), 0);
    const ratingsCount = generalStats.reduce((sum, stat) => sum + (stat?.number_of_ratings || 0), 0);

    return {
      ratings,
      // undefined rather than 0 so PerformanceSummary's `{value && ...}` checks don't render "0"
      overallRating: ratingsCount ? toOneDecimal(ratingsTotal / ratingsCount) : undefined,
      numberOfEvents: overview.number_of_notes || undefined,
      skillEntries
    };
  }
);

const NotesWithRatings = ({ personId, focusEventUuid, performanceOverview }) => {
  return (
    <div className="w-full">
      <div className="grid grid-cols-3 gap-6 items-start">
        <div className="col-span-2 min-w-0">
          <SubmittedFeedbacks personId={personId} focusEventUuid={focusEventUuid} />
        </div>

        <div className="col-span-1 min-w-0">
          <PerformanceSummary {...performanceOverview} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  performanceOverview: selectPerformanceOverview(state)
});

export default connect(mapStateToProps)(NotesWithRatings);
